import React, { forwardRef } from 'react';
import SearchableSelect from './ui/SearchableSelect';
import '../styles/components/FormField.css';

interface BaseFormFieldProps {
  label?: string;
  error?: string;
  help?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

interface InputProps extends BaseFormFieldProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local';
  leftIcon?: string;
  rightIcon?: string;
  onRightIconClick?: () => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  help,
  required,
  disabled,
  className = '',
  size: _size,
  leftIcon: _leftIcon,
  rightIcon: _rightIcon,
  onRightIconClick: _onRightIconClick,
  type = 'text',
  id,
  ...props
}, ref) => {
  const fieldId = id ?? `field-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className="form-label" htmlFor={fieldId}>
          {label}
          {required && <span className="form-required">*</span>}
        </label>
      )}
      <input
        ref={ref}
        id={fieldId}
        type={type}
        disabled={disabled}
        required={required}
        className={`form-input${error ? ' has-error' : ''}`}
        {...props}
      />
      {error && <span className="form-error-text">{error}</span>}
      {!error && help && <span className="form-helper">{help}</span>}
    </div>
  );
});

Input.displayName = 'Input';

interface DateTimeInputProps extends BaseFormFieldProps {
  /** ISO date, yyyy-mm-dd. */
  date: string;
  /** 24h time, HH:MM. May be empty — see below. */
  time: string;
  onChange: (date: string, time: string) => void;
}

/**
 * One field for a date and its time, instead of two sitting side by side.
 *
 * The two halves stay separate in storage (the database keeps date and time
 * as their own text columns), so this joins them for editing and splits them
 * again on the way out. Living here rather than in each form means the
 * splitting rule is written once.
 *
 * A date with NO time is a real state — most tasks have a due date and no
 * particular hour — and datetime-local cannot express it. So when the stored
 * time is empty, midnight is shown as a placeholder and an unedited midnight
 * is written back as empty. Without that, opening a task and saving it would
 * silently stamp 00:00 on everything that never had a time.
 */
export const DateTimeInput: React.FC<DateTimeInputProps> = ({
  label, error, help, required, disabled, className = '', date, time, onChange,
}) => {
  const fieldId = `field-${label ?? 'datetime'}`.replace(/\s+/g, '-').toLowerCase();
  const value = date ? `${date}T${time || '00:00'}` : '';

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className="form-label" htmlFor={fieldId}>
          {label}
          {required && <span className="form-required">*</span>}
        </label>
      )}
      <input
        id={fieldId}
        type="datetime-local"
        disabled={disabled}
        className={`form-input${error ? ' has-error' : ''}`}
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return onChange('', '');
          const [d, t = ''] = v.split('T');
          // Midnight on a row that never had a time means the user set the
          // date and left the clock alone — keep it timeless.
          onChange(d, t === '00:00' && !time ? '' : t);
        }}
      />
      {error && <span className="form-error-text">{error}</span>}
      {!error && help && <span className="form-helper">{help}</span>}
    </div>
  );
};

DateTimeInput.displayName = 'DateTimeInput';



interface SelectProps extends BaseFormFieldProps {
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  name?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  help,
  required,
  disabled,
  className = '',
  size: _size,
  options,
  placeholder,
  value,
  onChange,
}) => {
  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className="form-label">
          {label}
          {required && <span className="form-required">*</span>}
        </label>
      )}
      <SearchableSelect
        className={`form-select${error ? ' has-error' : ''}`}
        value={value ?? ''}
        onChange={v => onChange?.({ target: { value: v } })}
        disabled={disabled}
        placeholder={placeholder}
        options={options.filter(o => !o.disabled)}
      />
      {error && <span className="form-error-text">{error}</span>}
      {!error && help && <span className="form-helper">{help}</span>}
    </div>
  );
};

interface TextareaProps extends BaseFormFieldProps, Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  rows?: number;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  help,
  required,
  disabled,
  className = '',
  size: _size,
  rows = 3,
  resize = 'vertical',
  id,
  ...props
}, ref) => {
  const fieldId = id ?? `field-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className="form-label" htmlFor={fieldId}>
          {label}
          {required && <span className="form-required">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={fieldId}
        disabled={disabled}
        required={required}
        rows={rows}
        className={`form-input${error ? ' has-error' : ''} ff-resize-${resize}`}
        {...props}
      />
      {error && <span className="form-error-text">{error}</span>}
      {!error && help && <span className="form-helper">{help}</span>}
    </div>
  );
});

Textarea.displayName = 'Textarea';

interface CheckboxProps extends BaseFormFieldProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  children: React.ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({
  label: _label,
  error,
  help,
  required,
  disabled,
  className = '',
  size: _size,
  children,
  id,
  ...props
}, ref) => {
  const fieldId = id ?? `field-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`form-group mb-0 ${className}`}>
      <div className="form-checkbox-row">
        <input
          ref={ref}
          id={fieldId}
          type="checkbox"
          disabled={disabled}
          required={required}
          {...props}
        />
        <label htmlFor={fieldId} className="form-label form-checkbox-label">
          {children}
          {required && <span className="form-required">*</span>}
        </label>
      </div>
      {error && <span className="form-error-text">{error}</span>}
      {!error && help && <span className="form-helper">{help}</span>}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export { Input as FormInput, Select as FormSelect, Textarea as FormTextarea, Checkbox as FormCheckbox, DateTimeInput as FormDateTimeInput };
